import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { RouterLayoutProps, Routable } from './interfaces';
import {
  buildRoute,
  flattenNamedPages,
  flattenRoutePages,
  getValidRoutes,
  isRouterPage,
} from './utils';
import { RouteSet } from './RouteSet';
import { RouterProvider, RouterProviderProps } from './RouterProvider';

function searchForLayout(
  routables: Routable[],
  targetPath: string,
  currentPath: string,
  currentLayout?: React.FC<RouterLayoutProps>
): React.FC<RouterLayoutProps> | undefined {
  for (var j = 0; j < routables.length; j++) {
    const r = routables[j];
    const path = buildRoute(r.route, currentPath);

    if (matchPath(targetPath, { path, exact: r.exact }) != null) {
      const layout = r.layout ?? currentLayout;
      return isRouterPage(r)
        ? layout
        : searchForLayout(r.pages, targetPath, path, layout);
    }
  }

  return;
}

export interface RouterProps {
  routables: Routable[];
  isAuthenticated?: boolean;
  roles?: string[];
  type?: RouterProviderProps['type'];
}

const InternalRouter: React.VFC<RouterProps> = ({
  routables,
  isAuthenticated,
  roles,
}) => {
  const { pathname } = useLocation();
  const flattenedRoutes = flattenRoutePages(routables);
  const validRoutes = getValidRoutes(flattenedRoutes, isAuthenticated, roles);

  //Before we render our routes, we need to grab the relevant layout and render
  // it overtop of everything. While it would be much simpler to just carry the
  // relevant layout down with you and render it at the bottom of each route
  // (instead of reaching into the structure and pulling a layout out), the issue
  // is that by rendering the layout for every route, you essentially are
  // rendering a whole bunch of independent layouts all with different states.
  // In other words, every time you change routes, your layout state would reset.
  // Instead, we pull out a layout and render it in exactly the same place every
  // time, thus maintaining state between route changes.
  const Layout = searchForLayout(validRoutes, pathname, '');

  if (Layout != null) {
    const menuRoutes = flattenNamedPages(validRoutes);
    return (
      <Layout
        pages={menuRoutes}
        isAuthenticated={isAuthenticated}
        roles={roles}
      >
        <RouteSet routables={validRoutes} isAuthenticated={isAuthenticated} />
      </Layout>
    );
  }

  return <RouteSet routables={validRoutes} isAuthenticated={isAuthenticated} />;
};

export const Router: React.VFC<RouterProps> = ({ type, ...props }) => {
  return (
    <RouterProvider type={type}>
      <InternalRouter {...props} />
    </RouterProvider>
  );
};
